/**
 * VisitorIntelPixel — fires a single beacon per page view to the
 * /api/visitor-intel/track endpoint so the backend can reverse-IP-lookup
 * the visitor and identify B2B companies. Skips admin / authenticated /
 * partner / hand-raiser routes (no point tracking known users).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const EXCLUDED_PREFIXES = [
  '/admin',
  '/api/',
  '/partner/hq',
  '/partner/install',
  '/affiliate/dashboard',
  '/affiliate/login',
  '/affiliates/login',
  '/client-portal',
  '/client/',
  '/onboarding',
  '/winner-onboarding',
  '/runnerup-onboarding',
  '/login',
  '/signin',
  '/social-login',
];

function getSessionId() {
  let sid = sessionStorage.getItem('vi_sid');
  if (!sid) {
    sid = `vi_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('vi_sid', sid);
  }
  return sid;
}

function readUtm(search) {
  const p = new URLSearchParams(search || '');
  return {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
    utm_content: p.get('utm_content') || '',
  };
}

export default function VisitorIntelPixel() {
  const location = useLocation();

  useEffect(() => {
    if (!API) return;
    const path = location.pathname || '/';
    if (EXCLUDED_PREFIXES.some((p) => path.startsWith(p))) return;

    // Dedupe within the same browser session — React routing can mount this
    // component twice on first load (initial render + route resolution), and
    // we don't want to inflate the per-company visit_count aggregate.
    const dedupeKey = 'vi_seen_paths';
    let seen = [];
    try { seen = JSON.parse(sessionStorage.getItem(dedupeKey) || '[]'); } catch {}
    const visitKey = `${path}${location.search || ''}`;
    if (seen.includes(visitKey)) return;
    seen.push(visitKey);
    if (seen.length > 50) seen = seen.slice(-50);
    try { sessionStorage.setItem(dedupeKey, JSON.stringify(seen)); } catch {}

    const utm = readUtm(location.search);
    // Persist first-touch landing page across the session for attribution
    let landing = sessionStorage.getItem('vi_landing') || '';
    if (!landing) {
      landing = path + (location.search || '');
      sessionStorage.setItem('vi_landing', landing);
    }

    const payload = {
      path,
      referer: document.referrer || '',
      session_id: getSessionId(),
      landing_page: landing,
      ...utm,
    };

    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const sent = navigator.sendBeacon
        ? navigator.sendBeacon(`${API}/api/visitor-intel/track`, blob)
        : false;
      if (!sent) {
        fetch(`${API}/api/visitor-intel/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // Tracking is best-effort; never break the page on failure.
    }
  }, [location.pathname, location.search]);

  return null;
}
